import type { CentrifugeProgressMessage } from '@/hooks/useCentrifugeProgress';
import type { ProjectJob, ProjectJobState, ProjectResult } from '../api/types';
import { isJobDone } from './job-labels';

function upsertResult(
  existing: ProjectResult[],
  latest: ProjectResult,
): ProjectResult[] {
  const index = existing.findIndex((row) => row.url === latest.url);
  if (index < 0) return [...existing, latest];
  return existing.map((row, i) => (i === index ? latest : row));
}

function isProjectJobState(value: string | undefined): value is ProjectJobState {
  return (
    value === 'queued' ||
    value === 'active' ||
    value === 'completed' ||
    value === 'partial' ||
    value === 'failed' ||
    value === 'cancelled'
  );
}

/** Merge one URL result from Centrifugo into the open job (results table). */
export function mergeJobLiveProgress(
  job: ProjectJob,
  data: CentrifugeProgressMessage,
): ProjectJob {
  let next = job;

  if (data.state && isProjectJobState(data.state)) {
    next = { ...next, state: data.state };
  }

  if (data.successCount != null || data.failedCount != null) {
    next = {
      ...next,
      successCount: data.successCount ?? next.successCount,
      failedCount: data.failedCount ?? next.failedCount,
    };
  }

  if (data.latestResult) {
    next = {
      ...next,
      results: upsertResult(next.results ?? [], data.latestResult),
    };
  }

  return next;
}

/**
 * GET job را با ردیف‌های زندهٔ سوکت ادغام می‌کند.
 * کش/پاسخ ناقص نباید جدول را خالی کند.
 */
export function mergeJobFromFetch(
  prev: ProjectJob | null,
  next: ProjectJob,
): ProjectJob {
  if (!prev || prev.id !== next.id) return next;

  const prevResults = prev.results ?? [];
  const nextResults = next.results ?? [];

  let results = nextResults;
  if (prevResults.length > 0) {
    results = [...prevResults];
    for (const row of nextResults) {
      results = upsertResult(results, row);
    }
  }

  let merged: ProjectJob = { ...next, results };

  if (processedCount(prev) > processedCount(next)) {
    merged = {
      ...merged,
      successCount: prev.successCount,
      failedCount: prev.failedCount,
    };
  }

  if (isJobDone(prev.state) && !isJobDone(next.state)) {
    merged = { ...merged, state: prev.state };
  }

  if (prev.indexStatusSummary && !next.indexStatusSummary) {
    merged = { ...merged, indexStatusSummary: prev.indexStatusSummary };
  }

  return merged;
}

function processedCount(job: ProjectJob): number {
  return job.successCount + job.failedCount;
}

/** History table row: do not regress counts/state after socket or stale list GET. */
export function mergeJobListItemFromFetch(
  prev: ProjectJob,
  next: ProjectJob,
): ProjectJob {
  let merged: ProjectJob = { ...next };

  if (processedCount(prev) > processedCount(next)) {
    merged = {
      ...merged,
      successCount: prev.successCount,
      failedCount: prev.failedCount,
    };
  }

  if (isJobDone(prev.state) && !isJobDone(next.state)) {
    merged = { ...merged, state: prev.state };
  }

  if (prev.indexStatusSummary && !next.indexStatusSummary) {
    merged = { ...merged, indexStatusSummary: prev.indexStatusSummary };
  }

  return merged;
}

export function mergeJobsListFromFetch(
  prev: ProjectJob[],
  next: ProjectJob[],
): ProjectJob[] {
  const prevById = new Map(prev.map((job) => [job.id, job]));
  return next.map((job) => {
    const old = prevById.get(job.id);
    return old ? mergeJobListItemFromFetch(old, job) : job;
  });
}
